import { useCallback, useEffect, useRef, useState } from "react";
import type { SimilarHistoryEntry } from "@models/job";
import { mappingApi } from "@services/mappingApi";

/** Only examples this close (score > 80) are shown, mirroring the backend's
 *  display threshold (golden_min_similarity). */
const DISPLAY_MIN_SIMILARITY = 0.8;
const MAX_EXAMPLES = 5;

export interface UseRowExamplesResult {
  /** null until the first successful load. */
  examples: SimilarHistoryEntry[] | null;
  loading: boolean;
  /** Fetch on first use; a no-op once loaded or while in flight. */
  load: () => void;
}

/**
 * The golden examples behind a row's match ("you accepted a similar example
 * before"), fetched on demand from `GET .../results/{rowIndex}/examples` so
 * they never bloat the result-list payload.
 *
 * The effective match (hence its rank) can change while the row stays mounted,
 * e.g. the reviewer picks a different alternative. The cache is dropped on a
 * rank change so the next open refetches instead of showing the previous
 * match's history.
 */
export const useRowExamples = (
  jobId: string,
  rowIndex: number,
  rank: number | null,
): UseRowExamplesResult => {
  const [examples, setExamples] = useState<SimilarHistoryEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setExamples(null);
  }, [rank]);

  const load = useCallback((): void => {
    if (examples !== null || loading) return;
    setLoading(true);
    void mappingApi
      .getRowExamples(jobId, rowIndex, rank ?? undefined)
      .then((fetched) => {
        if (!mountedRef.current) return;
        const visible = fetched
          .filter(
            (e): e is SimilarHistoryEntry =>
              !!e &&
              typeof e.similarity === "number" &&
              e.similarity > DISPLAY_MIN_SIMILARITY,
          )
          .slice(0, MAX_EXAMPLES);
        setExamples(visible);
      })
      .catch(() => {
        if (mountedRef.current) setExamples([]);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [examples, loading, jobId, rowIndex, rank]);

  return { examples, loading, load };
};
